import { Dispatch, useCallback, useState } from "react";

import { SessionContextProps } from "../../Context/SessionContext";

export interface IUseSessionCacheParams extends Pick<SessionContextProps["helpers"], "saveToLocal" | "loadFromLocal"> {
  /** The cache key suffix to be used to cache data for the current session. */
  key: string;
}

export interface IUseSessionCacheResponse<TStoreType> {
  /** The value of the tracked object. */
  value: TStoreType | null;
  /** A callback to update the value of the tracked object and persist that record in the session cache. */
  setValue: Dispatch<TStoreType>;
}

/** A state hook which persists its value in the session cache.
 * @typeParam TStoreType - The type of the data stored in the session cache for the configured key.
 */
const useSessionCache = <TStoreType>({
  key,
  saveToLocal,
  loadFromLocal
}: IUseSessionCacheParams): IUseSessionCacheResponse<TStoreType> => {
  const [value, setValue] = useState<TStoreType | null>(() => loadFromLocal<TStoreType>(key) ?? null);
  const handleSet = useCallback((update: TStoreType | null) => {
    setValue(update);
    saveToLocal(update, key);
  }, []);
  return { value, setValue: handleSet };
};

export default useSessionCache;
